import { zodResolver } from '@hookform/resolvers/zod';
import { useGetApiSettingsHomeLocation, usePutApiSettingsHomeLocation } from '@smarthome/api';
import { ArrowLeft, MapPin, Save, AlertCircle, Globe } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { Button, TextField, Select, useToast } from '@/components/ui';

// Common timezones list
const TIMEZONES = [
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'Europe/London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Europe/Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (CET/CEST)' },
  { value: 'Europe/Amsterdam', label: 'Europe/Amsterdam (CET/CEST)' },
  { value: 'Europe/Brussels', label: 'Europe/Brussels (CET/CEST)' },
  { value: 'Europe/Rome', label: 'Europe/Rome (CET/CEST)' },
  { value: 'Europe/Madrid', label: 'Europe/Madrid (CET/CEST)' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (CET/CEST)' },
  { value: 'Europe/Oslo', label: 'Europe/Oslo (CET/CEST)' },
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki (EET/EEST)' },
  { value: 'Europe/Athens', label: 'Europe/Athens (EET/EEST)' },
  { value: 'Europe/Moscow', label: 'Europe/Moscow (MSK)' },
  { value: 'America/New_York', label: 'America/New_York (EST/EDT)' },
  { value: 'America/Chicago', label: 'America/Chicago (CST/CDT)' },
  { value: 'America/Denver', label: 'America/Denver (MST/MDT)' },
  { value: 'America/Los_Angeles', label: 'America/Los_Angeles (PST/PDT)' },
  { value: 'America/Phoenix', label: 'America/Phoenix (MST)' },
  { value: 'America/Toronto', label: 'America/Toronto (EST/EDT)' },
  { value: 'America/Vancouver', label: 'America/Vancouver (PST/PDT)' },
  { value: 'America/Sao_Paulo', label: 'America/Sao_Paulo (BRT)' },
  { value: 'Asia/Dubai', label: 'Asia/Dubai (GST)' },
  { value: 'Asia/Kolkata', label: 'Asia/Kolkata (IST)' },
  { value: 'Asia/Singapore', label: 'Asia/Singapore (SGT)' },
  { value: 'Asia/Hong_Kong', label: 'Asia/Hong_Kong (HKT)' },
  { value: 'Asia/Shanghai', label: 'Asia/Shanghai (CST)' },
  { value: 'Asia/Tokyo', label: 'Asia/Tokyo (JST)' },
  { value: 'Asia/Seoul', label: 'Asia/Seoul (KST)' },
  { value: 'Australia/Sydney', label: 'Australia/Sydney (AEST/AEDT)' },
  { value: 'Australia/Melbourne', label: 'Australia/Melbourne (AEST/AEDT)' },
  { value: 'Australia/Perth', label: 'Australia/Perth (AWST)' },
  { value: 'Pacific/Auckland', label: 'Pacific/Auckland (NZST/NZDT)' },
];

// Form schema
const homeLocationSchema = z.object({
  label: z.string().min(1, 'Label is required').max(100),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  timezone: z.string().min(1, 'Timezone is required'),
});

type HomeLocationFormData = z.infer<typeof homeLocationSchema>;

export function HomeLocationSettingsPage() {
  const { addToast } = useToast();

  const { data, isLoading, isError, refetch } = useGetApiSettingsHomeLocation();
  const location = data?.location;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isDirty },
  } = useForm<HomeLocationFormData>({
    resolver: zodResolver(homeLocationSchema),
    defaultValues: {
      label: '',
      latitude: 0,
      longitude: 0,
      timezone: 'UTC',
    },
  });

  // Initialize form from API data
  useEffect(() => {
    if (location) {
      reset({
        label: location.label ?? '',
        latitude: location.latitude ?? 0,
        longitude: location.longitude ?? 0,
        timezone: location.timezone ?? 'UTC',
      });
    }
  }, [location, reset]);

  const { mutate: setLocation, isPending: isSaving } = usePutApiSettingsHomeLocation({
    mutation: {
      onSuccess: () => {
        addToast({ message: 'Home location saved', variant: 'success' });
        refetch();
      },
      onError: (err: Error) => {
        addToast({
          message: err.message ?? 'Failed to save location',
          variant: 'error',
        });
      },
    },
  });

  const onSubmit = (data: HomeLocationFormData) => {
    setLocation({
      data: {
        label: data.label,
        latitude: data.latitude,
        longitude: data.longitude,
        timezone: data.timezone,
      },
    });
  };

  const handleGetCurrentLocation = () => {
    if (!navigator.geolocation) {
      addToast({ message: 'Geolocation is not supported by your browser', variant: 'error' });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setValue('latitude', Math.round(position.coords.latitude * 10000) / 10000, {
          shouldDirty: true,
        });
        setValue('longitude', Math.round(position.coords.longitude * 10000) / 10000, {
          shouldDirty: true,
        });
        addToast({ message: 'Location detected', variant: 'success' });
      },
      (error) => {
        addToast({
          message: `Failed to get location: ${error.message}`,
          variant: 'error',
        });
      }
    );
  };

  const hasLocation = location && location.latitude !== undefined;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Link
          to="/settings"
          className="rounded-lg p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:text-zinc-500 dark:hover:bg-white/[0.04] dark:hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
            Home Location
          </h1>
          <p className="mt-1 text-[15px] text-gray-500 dark:text-zinc-500">
            Used for sunrise/sunset calculations and day/night scene conditions
          </p>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500 dark:border-zinc-700" />
        </div>
      )}

      {/* Error state */}
      {isError && (
        <div className="rounded-lg bg-red-50 p-6 text-center dark:bg-red-500/10">
          <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-500" />
          <p className="text-sm text-red-600 dark:text-red-400">
            Failed to load home location settings
          </p>
          <button
            onClick={() => refetch()}
            className="mt-3 text-sm text-blue-500 hover:text-blue-400"
          >
            Try again
          </button>
        </div>
      )}

      {/* Form */}
      {!isLoading && !isError && (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Empty state callout */}
          {!hasLocation && (
            <div className="flex items-start gap-3 rounded-lg bg-blue-50 p-4 dark:bg-blue-500/10">
              <Globe className="mt-0.5 h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
                  Home location not configured
                </p>
                <p className="mt-1 text-sm text-blue-600 dark:text-blue-300/80">
                  Configure your home location to enable day/night and sunrise/sunset scene
                  conditions. This information is stored locally and never shared.
                </p>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-gray-200 bg-white p-6 dark:border-white/[0.06] dark:bg-[#18181b]">
            <div className="space-y-5">
              {/* Label */}
              <TextField
                label="Label"
                {...register('label')}
                error={errors.label?.message}
                placeholder="e.g., Home, Office"
              />

              {/* Coordinates */}
              <div className="grid gap-4 sm:grid-cols-2">
                <TextField
                  label="Latitude"
                  type="number"
                  step="0.0001"
                  {...register('latitude', { valueAsNumber: true })}
                  error={errors.latitude?.message}
                  placeholder="e.g., 52.3676"
                />
                <TextField
                  label="Longitude"
                  type="number"
                  step="0.0001"
                  {...register('longitude', { valueAsNumber: true })}
                  error={errors.longitude?.message}
                  placeholder="e.g., 4.9041"
                />
              </div>

              {/* Get current location button */}
              <Button
                type="button"
                variant="outline"
                onClick={handleGetCurrentLocation}
                leftIcon={<MapPin className="h-4 w-4" />}
              >
                Use Current Location
              </Button>

              {/* Timezone */}
              <Select
                label="Timezone"
                value={watch('timezone')}
                onChange={(v) => setValue('timezone', v, { shouldDirty: true })}
                options={TIMEZONES}
                error={errors.timezone?.message}
                fullWidth
              />
            </div>
          </div>

          {/* Current values info */}
          {hasLocation && (
            <div className="rounded-lg bg-gray-50 p-4 dark:bg-zinc-800/50">
              <div className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                Current Settings
              </div>
              <div className="mt-2 grid gap-2 text-sm text-gray-600 dark:text-zinc-400 sm:grid-cols-2">
                <div>
                  <span className="text-gray-500 dark:text-zinc-500">Location: </span>
                  {location.label}
                </div>
                <div>
                  <span className="text-gray-500 dark:text-zinc-500">Timezone: </span>
                  {location.timezone}
                </div>
                <div>
                  <span className="text-gray-500 dark:text-zinc-500">Coordinates: </span>
                  {location.latitude?.toFixed(4)}, {location.longitude?.toFixed(4)}
                </div>
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6 dark:border-white/[0.06]">
            <Link to="/settings">
              <Button variant="secondary">Cancel</Button>
            </Link>
            <Button type="submit" loading={isSaving} disabled={!isDirty}>
              <Save className="mr-2 h-4 w-4" />
              Save Location
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
